/**
 * Wristband Fee System
 * Added ONLY at checkout time — never visible in cart.
 * Quantity matches tube count only (tax items excluded).
 */

(function() {
  const WRISTBAND_PRODUCTS = {
    'Gruene':     'nb-city-wristband',
    'The Shore':  'nb-city-wristband',
    'The Double': 'nb-city-wristband',
    'Comal':      'nb-city-wristband',
    'Riverbank':  'word-wristband',
    'Huaco':      'word-wristband',
    'TEST':       'test-wristband-fee'
  };

  const WRISTBAND_HANDLES = [
    'nb-city-wristband',
    'word-wristband',
    'test-wristband-fee'
  ];

  const NB_CITY_LOCATIONS = ['Gruene', 'The Shore', 'The Double', 'Comal'];
  const DEBUG = true;
  const CLEARED_FLAG = 'rnr_wristband_cleared';

  let isProcessing = false;

  function log(msg) {
    if (DEBUG) console.log('[Wristband Fee]:', msg);
  }

  // Stable-partition line item properties so customer-visible keys come first
  // and technical (underscore-prefixed) keys come last. Order within each group
  // is preserved. Controls display order in the Shopify admin order detail.
  function orderLineProperties(props) {
    const entries = Object.entries(props);
    const visible = entries.filter(([k]) => !k.startsWith('_'));
    const hidden  = entries.filter(([k]) =>  k.startsWith('_'));
    return Object.fromEntries([...visible, ...hidden]);
  }

  // ─────────────────────────────────────────────
  // Booking data
  // ─────────────────────────────────────────────

  function isTestCollection() {
    if (window.location.href.toLowerCase().includes('/collections/test')) return true;
    const m = document.querySelector('meta[property="og:product:collection"], meta[name="collection"]');
    return !!(m?.content?.toLowerCase().includes('test'));
  }

  function normalizeLocationName(loc) {
    if (!loc) return loc;
    const s = String(loc).trim();
    if (/^shore$/i.test(s) || /^the\s+shore$/i.test(s)) return 'The Shore';
    if (/^double$/i.test(s) || /^the\s+double$/i.test(s)) return 'The Double';
    return s;
  }

  function getBookingLocation() {
    try {
      const d = JSON.parse(localStorage.getItem('rockinRBookingData') || '{}');
      const loc = d.location || (isTestCollection() ? 'TEST' : null);
      return normalizeLocationName(loc);
    } catch (e) { return null; }
  }

  function getBookingDate() {
    try {
      return JSON.parse(localStorage.getItem('rockinRBookingData') || '{}').date || null;
    } catch (e) { return null; }
  }

  function getGuestCount() {
  try {
    const count = parseInt(JSON.parse(localStorage.getItem('rockinRBookingData') || '{}').guests, 10);
    return isNaN(count) || count < 1 ? null : count;
    } catch (e) { return null; }
  }

  // ─────────────────────────────────────────────
  // Date helpers
  // ─────────────────────────────────────────────

  function getMemorialDay(year) {
    const may31 = new Date(year, 4, 31);
    const dow = may31.getDay();
    return new Date(year, 4, 31 - (dow === 0 ? 6 : dow - 1));
  }

  function getLaborDay(year) {
    const sept1 = new Date(year, 8, 1);
    const dow = sept1.getDay();
    return new Date(year, 8, 1 + (dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow));
  }

  function isSameDate(d1, d2) {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth()    === d2.getMonth()    &&
           d1.getDate()     === d2.getDate();
  }

  function isWeekend(d) { return d.getDay() === 0 || d.getDay() === 6; }
  function inRange(d, s, e) { return d >= s && d <= e; }

  // ─────────────────────────────────────────────
  // Rules
  // ─────────────────────────────────────────────

  function isNBCityRequired(dateStr) {
    if (!dateStr) return false;
    try {
      const [y, m, d] = dateStr.split('-').map(Number);
      const date  = new Date(y, m - 1, d);
      const mem   = getMemorialDay(y);
      const lab   = getLaborDay(y);
      const jul4  = new Date(y, 6, 4);
      const jul3_2026 = new Date(2026, 6, 3);
      const start = new Date(mem); start.setDate(mem.getDate() - 3);
      const end   = new Date(lab); end.setDate(lab.getDate() + 6);
      if (isSameDate(date, jul3_2026)) return true;
      if (!inRange(date, start, end)) return false;
      if (isSameDate(date, mem))  return true;
      if (isSameDate(date, jul4)) return true;
      if (isSameDate(date, jul3_2026)) return true;
      if (isSameDate(date, lab))  return true;
      return isWeekend(date);
    } catch (e) { return false; }
  }

  // Huaco (WORD) — same weekend + holiday logic as NB City
  function isHuacoRequired(dateStr) {
    if (!dateStr) return false;
    try {
      const [y, m, d] = dateStr.split('-').map(Number);
      const date  = new Date(y, m - 1, d);
      const mem   = getMemorialDay(y);
      const lab   = getLaborDay(y);
      const jul4  = new Date(y, 6, 4);
      const start = new Date(mem); start.setDate(mem.getDate() - 3); // Fri before Memorial Day
      const end   = new Date(lab); end.setDate(lab.getDate() + 6);   // Sun after Labor Day
      if (!inRange(date, start, end)) return false;
      if (isSameDate(date, mem))  return true;
      if (isSameDate(date, jul4)) return true;
      if (isSameDate(date, lab))  return true;
      return isWeekend(date);
    } catch (e) { return false; }
  }

  function shouldAddWristband(location) {
    if (!location) return false;
    if (location === 'TEST') return true;
    if (NB_CITY_LOCATIONS.includes(location)) return isNBCityRequired(getBookingDate());
    if (location === 'Huaco')     return isHuacoRequired(getBookingDate());
    if (location === 'Riverbank') return true;
    return false;
  }

  // ─────────────────────────────────────────────
  // Cart item classification
  // ─────────────────────────────────────────────

  function isWristbandItem(item) {
    return WRISTBAND_HANDLES.includes(item.handle) ||
      (item.properties && item.properties._wristband_fee === 'true');
  }

  // Exclude Stream Orders tax items and cooler tubes from wristband count
  function isTaxItem(item) {
    const handle = (item.handle || '').toLowerCase();
    const title  = (item.product_title || item.title || '').toLowerCase();
    return handle.includes('_tax_') || handle.includes('-tax-') ||
           title.includes('_tax_')  || title.includes('-tax-');
  }

  function isCoolerTube(item) {
    const handle = (item.handle || '').toLowerCase();
    const title  = (item.product_title || item.title || '').toLowerCase();
    return handle.includes('cooler') || title.includes('tube for cooler') || title.includes('cooler tube');
  }

  function isTubeItem(item) {
    return !isWristbandItem(item) && !isTaxItem(item) && !isCoolerTube(item);
  }

  // ─────────────────────────────────────────────
  // Cart helpers
  // ─────────────────────────────────────────────

  async function getCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function removeAllWristbands(cart) {
    const items = cart.items.filter(i => isWristbandItem(i));
    for (const item of items) {
      log(`Removing wristband: ${item.handle} qty:${item.quantity} key:${item.key}`);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
    }
    return items.length;
  }

  function getTubeQuantity(cart) {
    const tubeItems = cart.items.filter(i => isTubeItem(i));
    const qty = tubeItems.reduce((sum, i) => sum + i.quantity, 0);
    log(`Tube count: ${qty} from items: ${tubeItems.map(i => i.handle + 'x' + i.quantity).join(', ')}`);
    return qty;
  }

  // ─────────────────────────────────────────────
  // Core: prepare wristband at checkout time
  // ─────────────────────────────────────────────

  async function prepareWristbandForCheckout(location) {
    const handle = WRISTBAND_PRODUCTS[location];
    if (!handle) { log(`No product mapped for: ${location}`); return false; }

    try {
      const cart = await getCart();
      log(`Cart before clear: [${cart.items.map(i => i.handle + 'x' + i.quantity).join(', ')}]`);
      const removed = await removeAllWristbands(cart);

      // Wait for Shopify to finish processing the removal before re-fetching
      if (removed > 0) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      
      const freshCart  = await getCart();
      const guestCount = getGuestCount();
      const tubeQty    = getTubeQuantity(freshCart);
      const qty        = guestCount ?? tubeQty;

      log(`Guest count: ${guestCount ?? 'not found'}, tube qty: ${tubeQty}, using: ${qty}`);

      if (qty === 0) {
        log('No guests/tubes — skipping wristband');
        return true;
      }

      // Step 3: fetch product
      const pRes = await fetch(`/products/${handle}.js`);
      if (!pRes.ok) { log(`Product not found: ${handle}`); return false; }
      const product = await pRes.json();
      if (!product.variants?.[0]) { log(`No variants: ${handle}`); return false; }

      // Step 4: add
      const date  = getBookingDate();
      const props = {
        _wristband_fee_location: location,
        _wristband_fee: "true",
        "City-required item": `Wristband Fee for ${location}`,
        _timestamp: Date.now().toString()
      };
      if (date) props._wristband_fee_date = date;
      if (location === 'TEST') props._test_collection = "true";

      log(`Adding ${qty}x ${handle} for ${location}`);

      const aRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: product.variants[0].id, quantity: qty, properties: orderLineProperties(props) }]
        })
      });

      if (aRes.ok) {
        log(`Wristband added successfully`);
        return true;
      }
      const err = await aRes.text();
      log(`Add failed: ${aRes.status} - ${err}`);
      return false;

    } catch (e) {
      log('prepareWristbandForCheckout error: ' + e.message);
      return false;
    }
  }

  // ─────────────────────────────────────────────
  // On page load: remove any stale wristbands.
  // On cart pages, reload once so the UI re-renders
  // clean — custom cart templates don't respond to
  // JS cart events; a reload is the only reliable fix.
  // ─────────────────────────────────────────────

  async function clearStaleWristbands() {
    try {
      const cart = await getCart();
      const count = cart.items.filter(i => isWristbandItem(i)).length;

      if (count === 0) {
        // No stale wristbands — clear the reload guard so future clears work
        sessionStorage.removeItem(CLEARED_FLAG);
        return;
      }

      log(`Found ${count} stale wristband(s) on page load — clearing`);
      await removeAllWristbands(cart);

      // Reload the page so the custom cart template re-fetches and renders clean.
      // sessionStorage guard prevents infinite reload loop.
      const alreadyReloaded = sessionStorage.getItem(CLEARED_FLAG);
      if (!alreadyReloaded) {
        sessionStorage.setItem(CLEARED_FLAG, '1');
        log('Reloading page to flush wristband from cart UI...');
        window.location.reload();
      } else {
        // Already reloaded once — something is re-adding the wristband. Don't loop.
        sessionStorage.removeItem(CLEARED_FLAG);
        log('Already reloaded once — skipping second reload');
        if (typeof window.refreshCart === 'function') window.refreshCart();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }

    } catch (e) {
      log('clearStaleWristbands error: ' + e.message);
    }
  }

  // ─────────────────────────────────────────────
  // Checkout detection — safe for SVG elements
  // ─────────────────────────────────────────────

  function getElClass(el) {
    try {
      const cn = el.className;
      if (!cn) return '';
      if (typeof cn === 'string') return cn.toLowerCase();
      if (typeof cn.baseVal === 'string') return cn.baseVal.toLowerCase();
      return '';
    } catch (e) { return ''; }
  }

  function isCheckoutElement(el) {
    if (!el || !el.tagName) return false;
    if (el.id === 'rnr-checkout-btn') return false;
    // Also skip if the click target is INSIDE the booking or season pass button
    // (e.g. the inner span label) — those flows manage their own wristband.
    try {
      if (el.closest && el.closest('#rnr-checkout-btn')) return false;
      if (el.closest && el.closest('#rnr-sp-buy-btn'))   return false;
    } catch (e) { /* closest can throw on detached nodes — ignore */ }
    try {
      const href = (el.getAttribute?.('href') || '').toLowerCase();
      const name = (el.getAttribute?.('name') || '').toLowerCase();
      const text = (el.textContent || '').toLowerCase().trim();
      const cls  = getElClass(el);

      return (
        href.includes('/checkout') ||
        name === 'checkout' ||
        el.hasAttribute?.('data-checkout') ||
        cls.includes('checkout') ||
        text === 'checkout' ||
        text === 'check out' ||
        text.startsWith('checkout')
      );
    } catch (e) { return false; }
  }

  function findCheckoutTrigger(el) {
    let current = el;
    for (let i = 0; i < 5; i++) {
      if (!current || !current.tagName) break;
      if (isCheckoutElement(current)) return current;
      current = current.parentElement;
    }
    return null;
  }

  async function handleCheckout(event, trigger) {
    if (isProcessing) { log('Already processing'); return; }

    const location = getBookingLocation();
    log(`Checkout triggered — location: ${location}`);

    if (!location || !shouldAddWristband(location)) {
      log('No wristband needed — passing through');
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    isProcessing = true;

    // Clear the reload guard so the next page load (checkout return) works cleanly
    sessionStorage.removeItem(CLEARED_FLAG);

    const success = await prepareWristbandForCheckout(location);
    log(`Wristband ${success ? 'SUCCESS' : 'FAILED'} — redirecting to /checkout`);

    isProcessing = false;
    window.location.href = '/checkout';
  }

  function setupCheckoutInterception() {
    // Clicks — catches buttons and links
    document.addEventListener('click', async function(event) {
      const trigger = findCheckoutTrigger(event.target);
      if (!trigger) return;
      log(`Checkout click: ${trigger.tagName} "${(trigger.textContent || '').trim().slice(0, 40)}"`);
      await handleCheckout(event, trigger);
    }, true);

    // Form submits — catches native form POSTs to /checkout
    document.addEventListener('submit', async function(event) {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = (form.getAttribute('action') || '').toLowerCase();
      const hasCheckoutBtn = !!form.querySelector('[name="checkout"]');
      if (!action.includes('/checkout') && !hasCheckoutBtn) return;
      log('Form submit to checkout intercepted');
      await handleCheckout(event, form);
    }, true);
  }

  // ─────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────

  function init() {
    log('Initializing wristband fee system (checkout-time only)');
    clearStaleWristbands();
    setupCheckoutInterception();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.rnrAddWristband       = prepareWristbandForCheckout;
  window.rnrShouldAddWristband = shouldAddWristband;
  window.rnrGetBookingLocation = getBookingLocation;
})();